import { CheckCircle, AlertCircle, Sparkles, Layout, Ruler, HardHat, FileText } from "lucide-react";
import productScreenshot from "../assets/product-screenshot-analysis.png";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const AnalysisReport = () => {
  const navigate = useNavigate();
  return (
    <div className="bg-white pt-12 relative overflow-hidden" id="services">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-slate-50 to-transparent" />
      
      <div className="container mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col lg:flex-row items-center gap-16 mb-24"
        >
          <div className="lg:w-1/2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-blue-50 text-blue-600 font-bold text-xs uppercase tracking-widest mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              Advanced Analytics Engine
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-8 leading-tight">
              Professional <span className="text-blue-600">Feasibility Audits</span> in 60 Seconds
            </h2>
            <p className="text-lg text-slate-600 mb-10 leading-relaxed">
              Why wait weeks for a preliminary survey? Our proprietary engine bridges the gap between raw GIS data and actionable architectural intelligence. Instantly resolve the variables that dictate project ROI.
            </p>

            <div className="space-y-6">
              {[
                { icon: Layout, title: "MFA & MDA Precision", desc: "Instantly calculated Floor Area and Development Area limits." },
                { icon: Ruler, title: "Hillside Constraints", desc: "Automated average slope checks based on official contour intervals." },
                { icon: HardHat, title: "ADU Capability", desc: "Verify secondary unit potential against updated state mandates." }
              ].map((f, i) => (
                <motion.div 
                  key={i} 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex gap-4 group"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all">
                    <f.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">{f.title}</h3>
                    <p className="text-sm text-slate-500">{f.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="lg:w-1/2 relative">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative z-10 rounded-3xl overflow-hidden shadow-2xl border border-slate-200"
            >
               <img 
                 onClick={() => navigate('/dashboard')}
                 src={productScreenshot} 
                 alt="Proprietary Analysis Interface" 
                 className="w-full h-auto cursor-pointer transition-transform hover:scale-[1.02] duration-500 rounded-lg shadow-inner"
               />
               {/* Overlay for even more "Premium" feel */}
               <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent pointer-events-none" />
            </motion.div>
            
            {/* Experience Floating Card */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="absolute -bottom-10 -left-10 bg-white p-6 rounded-2xl shadow-xl border border-blue-100 hidden md:block z-20 animate-bounce-slow"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 font-bold uppercase">Recent Audit</p>
                  <p className="text-sm font-black text-slate-900">Los Altos Hills, CA</p>
                </div>
              </div>
            </motion.div>
            
            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-blue-400/20 blur-[120px] -z-1" />
          </div>
        </motion.div>
      </div>
      <div className="bg-[#e3e6e8] border-y pb-5 pt-10 border-slate-100 relative overflow-hidden">
        {/* Subtle Background Glow for the Hub */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-blue-100/20 blur-[120px] pointer-events-none" />
        
        {/* Audit Modules Grid - Simplified 3D Neural Hub */}
        <div className="relative py-0 overflow-hidden">
          <motion.div 
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             className="text-center relative z-10"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50/50 backdrop-blur-md border border-blue-100 text-blue-600 text-xs font-black uppercase tracking-[0.2em] mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
              </span>
              System Core Active
            </div>
            <h3 className="text-5xl font-black text-slate-900 mb-6 tracking-tight">
              The <span className="text-transparent bg-clip-text bg-gradient-to-tr from-blue-600 via-indigo-500 to-blue-600 animate-gradient-x">Innovation Hub</span>
            </h3>
            <p className="text-slate-500 max-w-2xl mx-auto font-medium text-lg leading-relaxed">
              Proprietary architectural logic modules processing real-time GIS telemetry for mission-critical feasibility audits.
            </p>
          </motion.div>

          <div className="relative z-10">
            <motion.div 
              className="py-10"
            >
              {/* Single Row: Ultra Slow Marquee */}
              <div className="flex gap-8 animate-marquee-ultra-slow hover:[animation-play-state:paused]">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex gap-8 shrink-0">
                    {[
                      { name: "Terrain Slope (%)", icon: Ruler, id: "MOD-001" },
                      { name: "MDA/MFA Ratios", icon: Layout, id: "MOD-002" },
                      { name: "CDP Requirements", icon: FileText, id: "MOD-003" },
                      { name: "APN & Lot Data", icon: Sparkles, id: "MOD-004" },
                      { name: "Lot Unit Factor", icon: CheckCircle, id: "MOD-005" },
                      { name: "Permit History", icon: HardHat, id: "MOD-006" },
                      { name: "Soil Compatibility", icon: AlertCircle, id: "MOD-007" },
                      { name: "Hillside Overlay", icon: Layout, id: "MOD-008" }
                    ].map((module, idx) => (
                      <motion.div 
                        key={idx} 
                        whileHover={{ scale: 1.05, y: -5 }}
                        className="group relative bg-white/40 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/60 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_40px_80px_rgba(37,99,235,0.15)] transition-all duration-700 min-w-[320px] cursor-pointer overflow-hidden"
                      >
                        {/* Scanning Beam */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/10 to-transparent -translate-x-full group-hover:animate-scan pointer-events-none" />
                        
                        <div className="flex justify-between items-start mb-6">
                          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-lg group-hover:rotate-12 transition-transform duration-500">
                            <module.icon className="w-7 h-7" />
                          </div>
                          <span className="text-[10px] font-mono text-slate-400 font-bold bg-slate-100 px-2 py-1 rounded ring-1 ring-slate-200">{module.id}</span>
                        </div>
                        
                        <h4 className="font-black text-slate-800 text-xl tracking-tight mb-2 group-hover:text-blue-600 transition-colors">{module.name}</h4>
                        <div className="flex items-center gap-2">
                          <div className="h-1 w-12 bg-slate-100 rounded-full overflow-hidden">
                            <motion.div 
                              animate={{ x: [-48, 48] }}
                              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                              className="h-full w-full bg-blue-600 shadow-[0_0_8px_rgba(37,99,235,0.8)]"
                            />
                          </div>
                          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Status: Optimized</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Disclaimer / Protection Footer */}
        <div className="max-w-6xl mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-3 p-5 rounded-2xl bg-slate-50 border border-slate-200 text-slate-600 text-sm font-medium shadow-inner">
               <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
               <span>Proprietary Methodology: All calculations are derived from official TOWN GIS endpoints and are subject to town-specific overlay refinements.</span>
            </div>
        </div>
      </div>

      <style>{`
        .perspective-2000 {
          perspective: 2000px;
        }
        .transform-style-3d {
          transform-style: preserve-3d;
        }
        @keyframes marquee {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(calc(-100% - 2rem), 0, 0); }
        }
        @keyframes scan {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-marquee-ultra-slow {
          animation: marquee 80s linear infinite;
        }
        .animate-scan {
          animation: scan 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 5s ease infinite;
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default AnalysisReport;